import { computed, inject, Injector, Type } from '@angular/core';
import {
  LoadGridPayload,
  transformGridDateFormat,
  withGridConfigFeatures,
} from '@data-access/shared';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ShallowCamelCasePipe } from '@shared/pipes/shallow-camel-case.pipe';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import {
  BuildLinkFromValue,
  GridColumnCasePipe,
} from '@wcg/frontend-framework';
import { map, pipe, switchMap, tap } from 'rxjs';
import { withUserEntities } from './entities';
import {
  USER_GRID_STATE,
  USER_URL,
  userInitialState,
} from '@data-access/user-management/constants';
import {
  UpdateUser,
  User,
  UserSignalState,
} from '@data-access/user-management/types';
import { UserService } from '@data-access/user-management/services';

export const USER_SIGNAL_STORE = signalStore(
  withState(() => inject(USER_GRID_STATE)),
  withProps((store, _userService = inject(UserService)) => ({
    _gridCasePipe: new GridColumnCasePipe(),
    _userService,
    _shallowCamelCasePipe: new ShallowCamelCasePipe(),
    _buildLinkFromValue: new BuildLinkFromValue(),
    _wcgTableUtilService: inject(WcgTableUtilsService),
    _injector: inject(Injector),
    store,
  })),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withUserEntities()),
  withComputed(
    ({
      error,
      usersEntities,
      total,
      isLoading,
      currentUrl,
      gridType,
      store,
      _injector,
    }) => ({
      $currentUrl: computed(() => currentUrl()),
      $error: computed(() => error()),

      $users: computed(() => {
        const _users = usersEntities();
        if (!_users) {
          return [] as User[];
        }
        return transformGridDateFormat<User>(
          _injector,
          _users,
          store.columnsCopy(),
        );
      }),
      $gridType: computed(() => gridType()),
      $isLoading: computed(() => isLoading()),
      $totalCount: computed(() => total()),
      $isInternal: computed(() => store.isInternal()),
      $userUpdated: computed(() => store.userUpdated()),
      $updatedResult: computed(() => store.updatedResult()),
    }),
  ),
  withMethods(
    ({
      _userService,
      _shallowCamelCasePipe,
      _buildLinkFromValue,
      $currentUrl,
      _wcgTableUtilService,
      ...store
    }) => {
      const _loadUserGrid = rxMethod<LoadGridPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ params, updateColumns }) => {
            const updatedParams = params.set('is_internal', store.isInternal());

            return _userService.loadUserList($currentUrl(), updatedParams).pipe(
              map((response) => ({
                ...response,
                config: _shallowCamelCasePipe.transform(response.config),
              })),
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    currentStateDestroyed: false,
                    total: response.total,
                  });
                  store.loadUser(response.data);
                  store.loadConfigurations(
                    response.config,
                    updateColumns,
                    response.data?.[0],
                    _buildLinkFromValue,
                  );
                  store.setGridViews(response.views);
                },
                error: (error: Error) =>
                  patchState(store, { error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
              }),
            );
          }),
        ),
      );

      const _updateUser = rxMethod<{
        userObject: UpdateUser;
        userId: string;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, userUpdated: false })),
          switchMap(({ userObject, userId }) =>
            _userService.updateUser(userObject, userId).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error: error,
                    isLoading: false,
                    userUpdated: false,
                  }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  if (response.id) {
                    store.applyUserEntityUpdate(
                      userId,
                      response as unknown as User,
                    );
                    patchState(store, {
                      userUpdated: true,
                      updatedResult: {
                        userId: userId,
                        message: response.message,
                      },
                    });
                  }
                },
              }),
            ),
          ),
        ),
      );

      return {
        loadUserGrid: (payload: LoadGridPayload) => {
          return _loadUserGrid(payload);
        },
        resetGridSettings: (payload: LoadGridPayload) => {
          patchState(store, { searchString: '', isInternal: true });
          store.setDefaultGridView();
          _loadUserGrid(payload);
        },
        resetState: () =>
          patchState(store, {
            ...userInitialState,
            currentStateDestroyed: true,
          }),
        setURL: (isInternal: boolean) => {
          patchState(store, {
            currentUrl: USER_URL,
            isInternal,
          });
        },
        updateUser: (userObject: UpdateUser, userId: string) => {
          _updateUser({ userObject, userId });
        },
        updateUserGrid: (isInternal: boolean, payload: LoadGridPayload) => {
          patchState(store, { searchString: '', isInternal });
          store.setDefaultGridView();
          _loadUserGrid(payload);
        },
      };
    },
  ),
) satisfies Type<UserSignalState>;
